/**
 * Where the capability token is read from, and in what order.
 *
 * A separate module from `App.tsx` for one reason: this is the client half of RULINGS
 * **R67**, it is a security decision, and a security decision that can only be checked by
 * rendering a page is one nobody checks. `apps/web/test/shareToken.test.ts` drives it as a
 * pure function over URL strings.
 *
 * ## R67, and why the order is the order
 *
 * > The link an owner shares carries the token in the **URL fragment** —
 * > `https://…/list#t=<token>` — which a browser never transmits to any server... **Build
 * > consequence:** `apps/web` reads `location.hash` first and `?t=` second.
 * > — `docs/RULINGS.md`
 *
 * The fragment is not sent in the request line, so it is not in Netlify's access log, not in
 * any intermediary's, and not in a `Referer`. The query form is: it was the shape
 * `functions/handlers/shopping-lists.ts` used to mint, so it stays READ for compatibility
 * with a link somebody was already sent, and it is documented as the weaker one. Fragment
 * first means a link carrying both is treated as the safer shape.
 *
 * ## What this does NOT do
 *
 * It does not rewrite the address bar. Stripping the token after reading it would look
 * tidier and would break the page on reload, which is the wrong trade for a link somebody
 * was sent — R67 acknowledges the residual it leaves ("a fragment is invisible to the server
 * but not to the user's history") and answers it with `no-referrer`, `no-store` and
 * `noindex` on the route rather than with a disappearing URL.
 */

/** The query and fragment key. One letter, because it is typed into a phone by nobody. */
export const SHARE_TOKEN_KEY = 't';

/**
 * The token this URL presents, or null.
 *
 * `href` is passed in rather than read from `window` so the rule is testable and so a caller
 * cannot accidentally read a stale location. Both forms are parsed with `URLSearchParams`, so
 * `#t=x&debug=1`, a bare `#t=x`, and a percent-escaped token all behave the same, and a
 * fragment that is not a parameter list at all (`#top`) yields null and falls through.
 */
export function readShareToken(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  // R67: fragment first.
  const hash = url.hash;
  if (hash.length > 1) {
    const fromHash = new URLSearchParams(hash.slice(1)).get(SHARE_TOKEN_KEY);
    if (fromHash) return fromHash;
  }

  // The weaker, compatibility form.
  return url.searchParams.get(SHARE_TOKEN_KEY) || null;
}
